import { Observable } from '../store';

// Свой небольшой роутер на History API: таблица шаблонов, параметры, переходы.

export interface Location {
  pathname: string;
  search: string;
}

export interface RouteMatch<K extends string = string> {
  key: K;
  params: Record<string, string>;
}

export interface RouteDefinition<K extends string> {
  key: K;
  pattern: string;
}

interface CompiledRoute<K extends string> {
  key: K;
  regex: RegExp;
  names: string[];
}

export function compilePattern(pattern: string): { regex: RegExp; names: string[] } {
  const names: string[] = [];
  const source = pattern
    .split('/')
    .map((segment) => {
      if (segment.startsWith(':')) {
        names.push(segment.slice(1));
        return '([^/]+)';
      }
      return segment.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    })
    .join('/');
  return { regex: new RegExp(`^${source}/?$`), names };
}

export function matchRoutes<K extends string>(routes: readonly RouteDefinition<K>[], pathname: string): RouteMatch<K> | null {
  for (const route of routes) {
    const { regex, names } = compilePattern(route.pattern);
    const match = regex.exec(pathname);
    if (match) {
      const params: Record<string, string> = {};
      names.forEach((name, index) => {
        params[name] = decodeURIComponent(match[index + 1]!);
      });
      return { key: route.key, params };
    }
  }
  return null;
}

/** Подставляет параметры в шаблон: '/b/:blogId/posts' + {blogId} → '/b/42/posts'. */
export function buildPath(pattern: string, params: Record<string, string> = {}): string {
  return pattern.replace(/:([A-Za-z]+)/g, (_, name: string) => encodeURIComponent(params[name] ?? ''));
}

export interface HistoryLike {
  location: Location;
  push(path: string): void;
  replace(path: string): void;
  listen(handler: () => void): void;
}

export class BrowserHistory implements HistoryLike {
  get location(): Location {
    return { pathname: window.location.pathname, search: window.location.search };
  }

  push(path: string): void {
    window.history.pushState(null, '', path);
  }

  replace(path: string): void {
    window.history.replaceState(null, '', path);
  }

  listen(handler: () => void): void {
    window.addEventListener('popstate', handler);
  }
}

/** История в памяти: витрина компонентов и тесты, где адресной строки нет. */
export class MemoryHistory implements HistoryLike {
  private current: Location;
  private readonly handlers: (() => void)[] = [];

  constructor(initial = '/') {
    this.current = MemoryHistory.parse(initial);
  }

  get location(): Location {
    return this.current;
  }

  push(path: string): void {
    this.current = MemoryHistory.parse(path);
    for (const handler of this.handlers) handler();
  }

  replace(path: string): void {
    this.push(path);
  }

  listen(handler: () => void): void {
    this.handlers.push(handler);
  }

  private static parse(path: string): Location {
    const at = path.indexOf('?');
    return at === -1 ? { pathname: path, search: '' } : { pathname: path.slice(0, at), search: path.slice(at) };
  }
}

export interface NavigateOptions {
  replace?: boolean;
  keepScroll?: boolean;
}

export class Router<K extends string> {
  readonly location: Observable<Location>;
  private readonly compiled: CompiledRoute<K>[];

  constructor(
    private readonly routes: readonly RouteDefinition<K>[],
    private readonly history: HistoryLike,
  ) {
    this.location = new Observable(history.location);
    this.compiled = routes.map((route) => ({ key: route.key, ...compilePattern(route.pattern) }));
    history.listen(() => this.location.set(history.location));
  }

  match(pathname = this.location.get().pathname): RouteMatch<K> | null {
    for (const route of this.compiled) {
      const found = route.regex.exec(pathname);
      if (found) {
        const params: Record<string, string> = {};
        route.names.forEach((name, index) => {
          params[name] = decodeURIComponent(found[index + 1]!);
        });
        return { key: route.key, params };
      }
    }
    return null;
  }

  /** keepScroll — переход внутри той же страницы (открыть просмотр, сменить вкладку): прокрутка остаётся. */
  navigate(path: string, options: NavigateOptions = {}): void {
    const current = this.location.get();
    if (`${current.pathname}${current.search}` === path) return;
    if (options.replace) this.history.replace(path);
    else this.history.push(path);
    this.location.set(this.history.location);
    if (!options.keepScroll && typeof window !== 'undefined') window.scrollTo?.(0, 0);
  }

  path(key: K, params: Record<string, string> = {}): string {
    const route = this.routes.find((r) => r.key === key);
    if (!route) throw new Error(`Неизвестный маршрут ${key}`);
    return buildPath(route.pattern, params);
  }
}
